// Order a Test, Part 4 — renders a requisition schema generically, the
// same "one source of truth, never hardcode the field list a second
// time" discipline requisition_schema.py already uses for the
// Requisition Conversion tool's own web form. Field types match
// order_requisition_schema.py exactly: text, date, number, single_select,
// checkbox_group (supports exclusive_code), multi_select, radio_row, table
// (both the clinician-added-rows shape and the schema-fixed-rows shape,
// e.g. Toxicology's "poc_results" — see FixedRowsTableField below).
//
// Lab Orders dashboard — this is a byte-for-byte copy of
// labariumaiportal/frontend/src/components/DynamicRequisitionForm.js
// (including its `readOnly` prop), kept in sync by hand. The two
// frontends are separate deployable React apps with no shared package
// (same reason order_requisition_schema.py exists as a near-identical
// copy in both backends, per that module's own comment) — this app only
// ever uses it in `readOnly` mode, to render the exact same section/
// field layout the clinician saw, for the admin/staff order-detail view.
import React from "react";
import {
  Box, Typography, TextField, Checkbox, FormControlLabel, Radio,
  RadioGroup, Autocomplete, Chip, IconButton, Tooltip, Table, TableBody,
  TableCell, TableHead, TableRow, Button,
} from "@mui/material";
import { InfoOutlined, Delete, Add } from "@mui/icons-material";

const BRAND = { navy: "#00357a", gold: "#c9a96e" };

function FieldLabel({ label, help, required, isDark }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: isDark ? "#cbd5e1" : "#334155" }}>
        {label}
        {required && <Box component="span" sx={{ color: BRAND.gold, ml: 0.4 }}>*</Box>}
      </Typography>
      {help && (
        <Tooltip title={help} arrow placement="top">
          <InfoOutlined sx={{ fontSize: 14, color: isDark ? "#64748b" : "#94a3b8", cursor: "help" }} />
        </Tooltip>
      )}
    </Box>
  );
}

function inputSx(isDark) {
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px", fontSize: 14.5,
      bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#fff",
      "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.09)" : "rgba(0,53,122,0.14)" },
    },
  };
}

function BareTextField(props) {
  return <TextField hiddenLabel fullWidth size="small" {...props} />;
}

function CheckboxGroupField({ field, value, onChange, isDark, readOnly }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (code) => {
    if (readOnly) return;
    if (field.exclusive_code && code === field.exclusive_code) {
      onChange(selected.includes(code) ? [] : [code]);
      return;
    }
    let next = selected.filter((c) => c !== field.exclusive_code);
    next = next.includes(code) ? next.filter((c) => c !== code) : [...next, code];
    onChange(next);
  };
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, width: "100%" }}>
      {field.options.map((opt) => (
        <FormControlLabel
          key={opt.code}
          sx={{
            mr: 1.5,
            alignItems: "flex-start",
            flex: "1 1 280px",
            minWidth: 0,
            maxWidth: "100%",
          }}
          control={
            <Checkbox size="small" checked={selected.includes(opt.code)} onChange={() => toggle(opt.code)} disabled={readOnly}
              sx={{ color: BRAND.navy, "&.Mui-checked": { color: BRAND.navy }, pt: 0 }} />
          }
          label={
            <Typography sx={{ fontSize: 13.5, whiteSpace: "normal", overflowWrap: "break-word", wordBreak: "break-word" }}>
              {opt.label}
            </Typography>
          }
        />
      ))}
    </Box>
  );
}

function SingleSelectField({ field, value, onChange, readOnly }) {
  return (
    <RadioGroup row value={value || ""} onChange={(e) => onChange(e.target.value)}>
      {field.options.map((opt) => (
        <FormControlLabel key={opt.code} value={opt.code} disabled={readOnly}
          control={<Radio size="small" sx={{ color: BRAND.navy, "&.Mui-checked": { color: BRAND.navy } }} />}
          label={<Typography sx={{ fontSize: 13.5 }}>{opt.label}</Typography>} />
      ))}
    </RadioGroup>
  );
}

function MultiSelectCodesField({ field, value, onChange, isDark, readOnly }) {
  // Open-ended — options are SUGGESTIONS only (confirmed with the user,
  // "don't make things complicated... use like multi list because there
  // can be multiple ICD codes"). freeSolo lets any code be added, not
  // just ones in the suggestion list.
  const entries = Array.isArray(value) ? value : [];
  return (
    <Autocomplete
      multiple freeSolo size="small"
      disabled={readOnly}
      options={field.options}
      getOptionLabel={(opt) => (typeof opt === "string" ? opt : `${opt.code} — ${opt.label}`)}
      value={entries}
      onChange={(e, newValue) => {
        const normalized = newValue.map((v) =>
          typeof v === "string" ? { code: v.trim(), label: "" } : v
        ).filter((v) => v.code);
        onChange(normalized);
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip size="small" label={option.label ? `${option.code} — ${option.label}` : option.code}
            {...getTagProps({ index })} key={option.code + index} />
        ))
      }
      renderInput={(params) => (
        <TextField {...params} hiddenLabel placeholder="Search or type a diagnosis code" sx={inputSx(isDark)} />
      )}
    />
  );
}

function RadioRowField({ field, value, onChange, readOnly }) {
  const val = value || {};
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ whiteSpace: "normal" }} />
            {field.options.map((o) => <TableCell key={o.value} align="center" sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{o.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {field.statements.map((stmt, i) => (
            <TableRow key={i}>
              <TableCell sx={{ fontSize: 13, whiteSpace: "normal", minWidth: 220 }}>{stmt}</TableCell>
              {field.options.map((o) => (
                <TableCell key={o.value} align="center">
                  <Radio size="small" checked={val[i] === o.value} disabled={readOnly}
                    onChange={() => onChange({ ...val, [i]: o.value })}
                    sx={{ color: BRAND.navy, "&.Mui-checked": { color: BRAND.navy } }} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function MedicationTableField({ field, value, onChange, isDark, readOnly }) {
  const rows = Array.isArray(value) && value.length ? value : [{}];
  const updateRow = (i, colId, v) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, [colId]: v } : r));
    onChange(next);
  };
  const addRow = () => onChange([...rows, {}]);
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <Box>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {field.columns.map((c) => <TableCell key={c.id} sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{c.label}</TableCell>)}
              {!readOnly && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={i}>
                {field.columns.map((c) => (
                  <TableCell key={c.id}>
                    <TextField hiddenLabel size="small" type={c.type === "number" ? "number" : "text"}
                      slotProps={c.type === "number" ? { htmlInput: { min: c.min, max: c.max } } : undefined}
                      value={row[c.id] || ""} onChange={(e) => updateRow(i, c.id, e.target.value)}
                      disabled={readOnly}
                      sx={{ width: c.id === "medication" ? 160 : 100, ...inputSx(isDark) }} />
                  </TableCell>
                ))}
                {!readOnly && (
                  <TableCell>
                    <IconButton size="small" onClick={() => removeRow(i)}><Delete sx={{ fontSize: 16 }} /></IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      {!readOnly && (
        <Button size="small" startIcon={<Add sx={{ fontSize: 15 }} />} onClick={addRow}
          sx={{ mt: 1, textTransform: "none", fontSize: 13, color: BRAND.navy }}>
          Add medication
        </Button>
      )}
    </Box>
  );
}

// Toxicology's POC Results table (order_requisition_schema.py's
// "poc_results" field, the only field anywhere that declares
// `fixed_rows`) — unlike MedicationTableField's one-row-per-clinician-
// added-medication shape, this table's rows are FIXED by the schema
// itself and every row always renders; the clinician only toggles each
// row's per-column checkbox. Submitted value shape MUST match what
// requisition_pdf.py's _render_fixed_rows_table reads: an object keyed
// by each fixed row's own `code`, each value a {column_id: bool} map —
// not an array of free-text rows (that's MedicationTableField's shape,
// for fields with `columns` but no `fixed_rows`).
function FixedRowsTableField({ field, value, onChange, readOnly }) {
  const rowsByCode = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  // Unchecking must DELETE the column key (and the whole row entry once
  // its last column is cleared), never just set it to `false` — a row
  // left behind as e.g. {positive: false} is still a truthy dict, which
  // would wrongly satisfy order_requisition_schema.py's "at least one
  // toxicology test option selected" validation even though nothing is
  // actually checked on this table.
  const toggle = (code, colId) => {
    const current = { ...(rowsByCode[code] || {}) };
    if (current[colId]) {
      delete current[colId];
    } else {
      current[colId] = true;
    }
    const next = { ...rowsByCode };
    if (Object.keys(current).length === 0) {
      delete next[code];
    } else {
      next[code] = current;
    }
    onChange(next);
  };
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>Medication or Drug</TableCell>
            {field.columns.map((c) => <TableCell key={c.id} align="center" sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {field.fixed_rows.map((row) => (
            <TableRow key={row.code}>
              <TableCell sx={{ fontSize: 13, whiteSpace: "nowrap" }}>{row.label}</TableCell>
              {field.columns.map((c) => (
                <TableCell key={c.id} align="center">
                  <Checkbox size="small" checked={!!(rowsByCode[row.code] && rowsByCode[row.code][c.id])}
                    onChange={() => toggle(row.code, c.id)} disabled={readOnly}
                    sx={{ color: BRAND.navy, "&.Mui-checked": { color: BRAND.navy } }} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function RequisitionField({ field, value, onChange, isDark, readOnly }) {
  switch (field.type) {
    case "text":
      return <BareTextField sx={inputSx(isDark)} value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={readOnly} />;
    case "date":
      return <BareTextField type="date" sx={inputSx(isDark)} value={value || ""} onChange={(e) => onChange(e.target.value)}
        disabled={readOnly} slotProps={{ inputLabel: { shrink: true } }} />;
    case "number":
      return <BareTextField type="number" sx={{ maxWidth: 160, ...inputSx(isDark) }}
        slotProps={{ htmlInput: { min: field.min, max: field.max } }}
        value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={readOnly}
        InputProps={field.unit ? { endAdornment: <Typography sx={{ fontSize: 13, color: "#94a3b8", ml: 0.5 }}>{field.unit}</Typography> } : undefined} />;
    case "single_select":
      return <SingleSelectField field={field} value={value} onChange={onChange} readOnly={readOnly} />;
    case "checkbox_group":
      return <CheckboxGroupField field={field} value={value} onChange={onChange} isDark={isDark} readOnly={readOnly} />;
    case "multi_select":
      return <MultiSelectCodesField field={field} value={value} onChange={onChange} isDark={isDark} readOnly={readOnly} />;
    case "radio_row":
      return <RadioRowField field={field} value={value} onChange={onChange} readOnly={readOnly} />;
    case "table":
      return field.fixed_rows
        ? <FixedRowsTableField field={field} value={value} onChange={onChange} readOnly={readOnly} />
        : <MedicationTableField field={field} value={value} onChange={onChange} isDark={isDark} readOnly={readOnly} />;
    default:
      return null;
  }
}

// `excludeFieldIds` lets the caller skip fields it fills in itself
// (patient_info/ordering_provider — always server-populated from
// clinician_patient_records + the clinician's own account, never re-typed
// here — see OrderTest.js's own comment for why).
//
// `readOnly` — Lab Orders dashboard addition (admin + staff order-detail
// view): renders this EXACT same form, same section/field layout the
// clinician actually filled in, but every control disabled and `onChange`
// never wired to a real state update ­— a true "digital copy of the web
// form", not a second, parallel display component that could drift out
// of sync with this one over time. `onChange` may be omitted entirely
// when readOnly (nothing ever calls it).
export default function DynamicRequisitionForm({ schema, excludeSectionIds = [], data, onChange, isDark, readOnly = false }) {
  const setField = (fieldId, value) => { if (!readOnly) onChange({ ...data, [fieldId]: value }); };
  const sections = (schema || []).filter((s) => !excludeSectionIds.includes(s.id));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {sections.map((section) => (
        <Box key={section.id}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: BRAND.navy, mb: 1.5, letterSpacing: "0.03em" }}>
            {section.title}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {section.fields.map((field) => (
              <Box key={field.id}>
                <FieldLabel label={field.label} help={field.help} required={field.required} isDark={isDark} />
                <RequisitionField field={field} value={data[field.id]} onChange={(v) => setField(field.id, v)} isDark={isDark} readOnly={readOnly} />
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
